import React, { useState } from 'react';
import { Button } from './ui/button';
import { Mail, Phone, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    project: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    loading: false,
    message: '',
    type: '' // 'success' or 'error'
  });
  
  const [formTimestamp] = useState(Math.floor(Date.now() / 1000));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset status
    setFormStatus({ loading: true, message: '', type: '' });
    
    // Validation côté client
    if (!formData.name || !formData.email || !formData.project) {
      setFormStatus({
        loading: false,
        message: 'Tous les champs sont obligatoires.',
        type: 'error'
      });
      return;
    }
    
    if (formData.name.length < 2) {
      setFormStatus({
        loading: false,
        message: 'Le nom doit contenir au moins 2 caractères.',
        type: 'error'
      });
      return;
    }
    
    if (formData.project.length < 10) {
      setFormStatus({
        loading: false,
        message: 'Le message doit contenir au moins 10 caractères.',
        type: 'error'
      });
      return;
    }
    
    // Préparer les données pour l'envoi
    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('project', formData.project);
    formDataToSend.append('timestamp', formTimestamp);
    formDataToSend.append('website', ''); // Honeypot
    
    try {
      const response = await fetch('/contact.php', {
        method: 'POST',
        body: formDataToSend
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFormStatus({
          loading: false,
          message: data.message,
          type: 'success'
        });
        
        // Reset form
        setFormData({ name: '', email: '', project: '' });
      } else {
        setFormStatus({
          loading: false,
          message: data.message || 'Une erreur est survenue.',
          type: 'error'
        });
      }
    } catch (error) {
      setFormStatus({
        loading: false,
        message: 'Une erreur est survenue, veuillez réessayer plus tard.',
        type: 'error'
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-white w-full overflow-hidden">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1E3A5F] mb-4">
            Demandez votre ébauche gratuite
          </h2>
          <p className="text-lg sm:text-xl text-gray-600">
            Réponse rapide – Sans engagement
          </p>
          <p className="text-base text-gray-500 mt-2">
            Remplissez ce formulaire simple et je vous recontacte pour créer votre première ébauche gratuite
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="md:col-span-1 space-y-6">
            {/* Email Card */}
            <Card className="border-2 hover:border-[#FF9933]/30 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF9933]/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#FF9933]" />
                  </div>
                  <h3 className="font-semibold text-[#1E3A5F]">Email</h3>
                </div>
                <a
                  href="mailto:l.duperron@outlook.fr"
                  className="text-[#FF9933] hover:text-[#E68A2E] transition-colors duration-200 break-all"
                >
                  l.duperron@outlook.fr
                </a>
              </CardContent>
            </Card>

            {/* Phone Card */}
            <Card className="border-2 hover:border-[#FF9933]/30 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF9933]/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#FF9933]" />
                  </div>
                  <h3 className="font-semibold text-[#1E3A5F]">Téléphone</h3>
                </div>
                <a
                  href="tel:0745522835"
                  className="text-[#FF9933] hover:text-[#E68A2E] transition-colors duration-200"
                >
                  07 45 52 28 35
                </a>
              </CardContent>
            </Card>

            {/* Guarantee Card */}
            <Card className="bg-blue-50 border-2 border-[#FF9933]/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF9933] flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1E3A5F] mb-2">100% Sans risque</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Première ébauche gratuite. Vous voyez le résultat avant de payer.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-2">
            <Card className="border-2 hover:border-[#FF9933]/30 transition-all duration-300">
              <CardContent className="p-6 sm:p-8">
                {/* Message de status */}
                {formStatus.message && (
                  <div 
                    className={`mb-6 p-4 rounded-lg ${
                      formStatus.type === 'success' 
                        ? 'bg-green-50 border-2 border-green-500 text-green-800' 
                        : 'bg-red-50 border-2 border-red-500 text-red-800'
                    }`}
                  >
                    <p className="font-semibold flex items-center gap-2">
                      {formStatus.type === 'success' ? '✓' : '⚠'} {formStatus.message}
                    </p>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Input */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                      Votre nom *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Ex : Jean Dupont"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF9933] focus:outline-none transition-colors duration-200"
                    />
                  </div>

                  {/* Email Input */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                      Votre email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Ex : jean.dupont@email.fr"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF9933] focus:outline-none transition-colors duration-200"
                    />
                  </div>

                  {/* Project Description */}
                  <div>
                    <label htmlFor="project" className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                      Décrivez votre projet *
                    </label>
                    <textarea
                      id="project"
                      name="project"
                      required
                      value={formData.project}
                      onChange={handleChange}
                      rows="5"
                      placeholder="Décrivez simplement ce que vous faites..."
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF9933] focus:outline-none transition-colors duration-200 resize-none"
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={formStatus.loading}
                    className={`w-full text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-300 ${
                      formStatus.loading 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-[#FF9933] hover:bg-[#E68A2E]'
                    }`}
                  >
                    {formStatus.loading ? 'Envoi en cours...' : 'Recevoir ma première ébauche'}
                  </Button>

                  {/* Footer Note */}
                  <p className="text-center text-sm text-gray-500">
                    ✓ Réponse rapide · ✓ Sans carte bancaire
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
